import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { SparklesIcon, LightbulbIcon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
export interface AIInsight {
  title: string;
  content: string;
  type: 'opportunity' | 'warning' | 'neutral' | 'action';
  confidence?: number;
}
interface AIInsightCardProps {
  insights: AIInsight[];
  className?: string;
  title?: string;
  compact?: boolean;
  maxInitialInsights?: number;
  expanded?: boolean;
  onToggleExpand?: () => void;
}
const AIInsightCard: React.FC<AIInsightCardProps> = ({
  insights,
  className = '',
  title = 'AI Market Insights',
  compact = false,
  maxInitialInsights = 3,
  expanded: controlledExpanded,
  onToggleExpand
}) => {
  const [internalExpanded, setInternalExpanded] = useState(!compact);
  const [showAllInsights, setShowAllInsights] = useState(false);
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const handleToggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };
  if (!insights || insights.length === 0) return null;
  const displayedInsights = showAllInsights ? insights : insights.slice(0, maxInitialInsights);
  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity':
        return <TrendingUpIcon className="h-4 w-4 text-green-500" data-id="element-55" />;
      case 'warning':
        return <AlertCircleIcon className="h-4 w-4 text-amber-500" data-id="element-56" />;
      case 'neutral':
        return <LightbulbIcon className="h-4 w-4 text-blue-500" data-id="element-57" />;
      case 'action':
        return <CheckCircleIcon className="h-4 w-4 text-purple-500" data-id="element-58" />;
      default:
        return <LightbulbIcon className="h-4 w-4 text-blue-500" data-id="element-59" />;
    }
  };
  const getInsightBadge = (type: string) => {
    switch (type) {
      case 'opportunity':
        return <Badge variant="success" data-id="element-60">Opportunity</Badge>;
      case 'warning':
        return <Badge variant="warning" data-id="element-61">Warning</Badge>;
      case 'neutral':
        return <Badge variant="secondary" data-id="element-62">Insight</Badge>;
      case 'action':
        return <Badge variant="default" data-id="element-63">Action Item</Badge>;
      default:
        return <Badge variant="secondary" data-id="element-64">Insight</Badge>;
    }
  };
  return <Card className={`${className} border-blue-200 dark:border-blue-800 w-full`} data-id="element-65">
      <CardContent className="p-4" data-id="element-66">
        <div className="flex items-center justify-between mb-3" data-id="element-67">
          <div className="flex items-center" data-id="element-68">
            <SparklesIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-69" />
            <h3 className="font-medium text-base" data-id="element-70">{title}</h3>
          </div>
          {compact && <button onClick={handleToggleExpand} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted" data-id="element-71">
              {expanded ? <ChevronUpIcon className="h-4 w-4" data-id="element-72" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-73" />}
            </button>}
        </div>
        {expanded && <div className="space-y-3" data-id="element-74">
            {displayedInsights.map((insight, index) => <div key={index} className="p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border border-blue-100 dark:border-blue-800" data-id="element-75">
                <div className="flex justify-between items-start" data-id="element-76">
                  <div className="flex items-start" data-id="element-77">
                    {getInsightIcon(insight.type)}
                    <div className="ml-2" data-id="element-78">
                      <div className="font-medium text-sm" data-id="element-79">{insight.title}</div>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-80">
                        {insight.content}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-81">
                    {getInsightBadge(insight.type)}
                    {insight.confidence && <div className="text-xs text-muted-foreground mt-1 text-right" data-id="element-82">
                        {insight.confidence}% confidence
                      </div>}
                  </div>
                </div>
              </div>)}
            {insights.length > maxInitialInsights && <button onClick={() => setShowAllInsights(!showAllInsights)} className="text-sm text-blue-500 hover:text-blue-700 flex items-center justify-center w-full py-1 mt-1" data-id="element-83">
                {showAllInsights ? <>Show less</> : <>
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-84" />
                    Show {insights.length - maxInitialInsights} more insights
                  </>}
              </button>}
          </div>}
        {!expanded && <div className="text-sm text-muted-foreground" data-id="element-85">
            {insights.length} AI-generated insights available. Click to expand.
          </div>}
      </CardContent>
    </Card>;
};
export default AIInsightCard;